import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import moment from 'moment';
import { getCollectionSettings } from '../../modules/selectors';
import { changeCollectionState, updateCollectionByPath } from '../../modules/actions';

export type SettingsFieldValue = string | number | boolean | string[];

// Field-level update helpers of the Settings tab (extracted verbatim from
// SettingsSection; names kept so the JSX call sites stay unchanged).
export const useCollectionSettingsFieldUpdate = () => {
  const dispatch = useDispatch();
  const collection = useSelector(getCollectionSettings);

  // optimistic redux-only change
  const changeFormHandler = useCallback(
    (path: string, value: SettingsFieldValue) => dispatch(changeCollectionState({ path, value })),
    [dispatch]
  );

  // persist whatever redux currently holds under `path` (used on blur)
  const saveFieldHandler = useCallback(
    (path: string) => {
      dispatch(
        updateCollectionByPath({
          path,
          value: get(collection, path, '')
        })
      );
    },
    [dispatch, collection]
  );

  // optimistic change + persist in one go
  const changeWithUpdate = useCallback(
    (path: string, value: string | boolean) => {
      if (path === 'expirationDate' && typeof value === 'string' && value) {
        value = moment(value).endOf('day').toISOString();
      }

      changeFormHandler(path, value);
      dispatch(updateCollectionByPath({ path, value }));
    },
    [dispatch, changeFormHandler]
  );

  return { changeFormHandler, saveFieldHandler, changeWithUpdate };
};
